import * as React from 'react';

import styled from 'styled-components';

import { Button } from '../Button';
import { Icon } from '../Icon';

export interface SubNavItem {
  label: string;
  url: string;
  isActive?: boolean;
}

interface SubNavProps {
  items: SubNavItem[];
  className?: string;
}

/**
 * Horizontal sub-navigation component with scrollable items and active state
 */
export const SubNav: React.FC<SubNavProps> = ({ items, className }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const hasRestoredPositionRef = React.useRef(false);
  const [showLeftButton, setShowLeftButton] = React.useState(false);
  const [showRightButton, setShowRightButton] = React.useState(false);

  // Generate a unique key for this SubNav instance based on the items
  const subNavKey = React.useMemo(() => {
    return `subnav-${items.map((item) => item.url).join('-')}`;
  }, [items]);

  const checkScrollButtons = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;

      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
    }
  }, []);

  const saveScrollPosition = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      sessionStorage.setItem(subNavKey, scrollLeft.toString());
    }
  }, [subNavKey]);

  const scrollToActiveItem = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeIndex = items.findIndex((item) => item.isActive);

      if (activeIndex !== -1) {
        // Get all button elements within the scroll container
        const buttons = container.querySelectorAll('a, button');
        const activeButton = buttons[activeIndex] as HTMLElement;

        if (activeButton) {
          const containerRect = container.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();

          // Calculate if the active button is outside the visible area or partially visible
          const isButtonLeftOfContainer = buttonRect.left < containerRect.left;
          const isButtonRightOfContainer = buttonRect.right > containerRect.right;

          // Check if button is partially visible (less than 80% visible)
          const buttonWidth = buttonRect.width;
          const visibleWidth =
            Math.min(buttonRect.right, containerRect.right) -
            Math.max(buttonRect.left, containerRect.left);
          const isPartiallyVisible = visibleWidth > 0 && visibleWidth < buttonWidth;

          if (isButtonLeftOfContainer || isButtonRightOfContainer || isPartiallyVisible) {
            // Scroll to bring the active button into view and centre it
            activeButton.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }
      }
    }
  }, [items]);

  React.useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [checkScrollButtons, items]);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      // Save scroll position on scroll
      container.addEventListener('scroll', saveScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        container.removeEventListener('scroll', saveScrollPosition);
      };
    }
  }, [checkScrollButtons, saveScrollPosition]);

  // Apply scroll position immediately when ref is available
  const applyInitialScrollPosition = React.useCallback(() => {
    if (scrollContainerRef.current && !hasRestoredPositionRef.current) {
      const savedPosition = sessionStorage.getItem(subNavKey);
      if (savedPosition !== null) {
        const scrollLeft = parseInt(savedPosition, 10);
        scrollContainerRef.current.scrollLeft = scrollLeft;
        hasRestoredPositionRef.current = true;
      }
    }
  }, [subNavKey]);

  // Restore scroll position immediately when component mounts
  React.useEffect(() => {
    // Apply immediately and also on next frame to ensure it takes effect
    applyInitialScrollPosition();
    const frameId = requestAnimationFrame(() => {
      applyInitialScrollPosition();
    });

    return () => cancelAnimationFrame(frameId);
  }, [applyInitialScrollPosition]);

  // Scroll to active item after position restoration or if no position was restored
  React.useEffect(() => {
    // Use requestAnimationFrame for immediate execution
    const frameId = requestAnimationFrame(() => {
      scrollToActiveItem();
    });

    return () => cancelAnimationFrame(frameId);
  }, [scrollToActiveItem]);

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <SubNavWrapper className={className}>
      <div className="content">
        {showLeftButton && (
          <ScrollButton onClick={handleScrollLeft} aria-label="Scroll left" $position="left">
            <Icon iconId="chevron-left" iconSize="20px" />
          </ScrollButton>
        )}

        <ScrollContainer ref={scrollContainerRef}>
          {items.map((item, index) => (
            <Button
              key={index}
              style={item.isActive ? 'primary' : 'grey'}
              size="small"
              label={item.label}
              href={item.url}
            />
          ))}
        </ScrollContainer>

        {showRightButton && (
          <ScrollButton onClick={handleScrollRight} aria-label="Scroll right" $position="right">
            <Icon iconId="chevron-right" iconSize="20px" />
          </ScrollButton>
        )}
      </div>
    </SubNavWrapper>
  );
};

const SubNavWrapper = styled.div`
  position: relative;
  background: var(--white);
  padding: 28px 0px;
  box-sizing: border-box;
  overflow: hidden;

  .content {
    position: relative;
    @media (max-width: 768px) {
      margin: 0 25px;
    }
  }

  @media (max-width: 768px) {
    padding: 17px 0px 36px 0px;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  max-width: 100%;
  align-items: center;

  &::-webkit-scrollbar {
    display: none;
  }

  flex: 1;
`;

const ScrollButton = styled.button<{ $position: 'left' | 'right' }>`
  position: absolute;
  ${({ $position }) => $position}: 0;
  top: 50%;
  transform: translateY(-50%)
    translateX(${({ $position }) => ($position === 'left' ? '-50%' : '50%')});
  width: 38px;
  height: 38px;
  padding: ${({ $position }) => ($position === 'left' ? '9px 10px 9px 8px' : '9px 8px 9px 10px')};
  border-radius: 50%;
  background: var(--white);
  border: 0px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  svg {
    color: var(--stile-grey-5);
  }

  @media (max-width: 768px) {
    top: -1px;
    transform: translateX(${({ $position }) => ($position === 'left' ? '-50%' : '50%')});
    width: 34px;
    height: 34px;
    padding: ${({ $position }) => ($position === 'left' ? '8px 9px 8px 7px' : '8px 7px 8px 9px')};
  }

  &:hover {
    background: var(--stile-grey-1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-50%)
      translateX(${({ $position }) => ($position === 'left' ? '-50%' : '50%')})
      translateX(${({ $position }) => ($position === 'left' ? '-1px' : '1px')});

    @media (max-width: 768px) {
      transform: translateX(${({ $position }) => ($position === 'left' ? '-50%' : '50%')})
        translateX(${({ $position }) => ($position === 'left' ? '-1px' : '1px')});
    }

    svg {
      color: var(--stile-green-3);
    }
  }
`;
